import { useMediaQuery } from "@mui/material";
import { useIntersectionObserver, useIsClient } from "@uidotdev/usehooks";
import Cookies from "js-cookie";
import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Color from "../../../assets/colors";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import {
  bookmarkJobCompany,
  unBookmarkJobCompany,
} from "../../../redux/actions/company_action";
import { updateSelectedJob } from "../../../redux/actions/job_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../redux/actions/navbar_action";
import { checkAuthentication } from "../../../redux/actions/user_action";
import {} from "../../error404/styles";
import {
  ArrowLeftIconStyled,
  ArrowRightIconStyled,
  BookmarkFilledIconStyled,
  BookmarkIconStyled,
  ButtonStyled,
  CircularProgressStyled,
  CompanyLogo,
  FlexContainer,
  HScrollContainer,
  SectionContainer,
  TextStyled,
} from "../styles";
import {
  ButtonContainer,
  FilterChip,
  HighlightChip,
  JobCard,
  JobContainer,
  JobInfoChip,
  OverlayContainer,
  PaginationButton,
  PaginationContainer,
  SparklesIconStyled,
} from "./styles";

function CompanyJobs(props) {
  const { setCurrentTab } = props || {};
  const isDesktop = useMediaQuery("(min-width: 1280px)");

  const isClient = useIsClient();

  const [ref, entry] = useIntersectionObserver();
  const [refLeft, entryLeft] = useIntersectionObserver();
  const [refRight, entryRight] = useIntersectionObserver({
    threshold: 0.925,
    root: null,
    rootMargin: "0px",
  });

  entry?.isIntersecting ? setCurrentTab(5) : null;
  const scrollContainerRef = useRef(null);
  const showButtonLeft = entryLeft?.isIntersecting;
  const showButtonRight = entryRight?.isIntersecting;

  const companyJobs = useSelector((state) => state?.companies?.companyJobs);
  const specialisation = useSelector((state) => state?.jobs?.specialisation);

  const itemsPerPage = 9;
  const [page, setPage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState("");

  const filteredJobList =
    selectedFilter === ""
      ? companyJobs
      : companyJobs.filter((job) =>
          job.tracks.some((track) => track.id === selectedFilter.toString())
        );

  const totalPages = Math.ceil(filteredJobList.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const companyJobsList = filteredJobList.slice(startIndex, endIndex);

  const getSpecialisationList = useMemo(() => {
    const mapSubCategories = (categoryObj) =>
      categoryObj?.subSpecialisations?.map((subCategoryObj) => ({
        id: subCategoryObj?.id,
        parentCategory: categoryObj?.name,
        subCategory: subCategoryObj?.name,
        name: `${categoryObj?.name} - ${subCategoryObj?.name}`,
      }));
    return specialisation?.flatMap(mapSubCategories).filter(Boolean) || [];
  }, [specialisation]);

  const jobTrackList = useMemo(
    () => [
      ...new Set(
        companyJobs.flatMap((job) => job.tracks.map((track) => track.id))
      ),
    ],
    [companyJobs]
  );

  const filteredSpecialisation = useMemo(
    () =>
      getSpecialisationList.filter((track) =>
        jobTrackList.includes(track.id.toString())
      ),
    [getSpecialisationList, jobTrackList]
  );

  const lastFilterItem = useMemo(
    () => filteredSpecialisation.length - 1,
    [filteredSpecialisation]
  );

  function handleFilterClick(e, specialisationDetail) {
    e.stopPropagation();

    if (specialisationDetail === "") {
      sendTrackingEvent({
        event: "CE_apply-filter-cdp",
        parent_specialization: "all",
        sub_specialization: "all",
      });
      setPage(1);
      setSelectedFilter("");
      return;
    }

    sendTrackingEvent({
      event: "CE_apply-filter-cdp",
      parent_specialization: specialisationDetail?.parentCategory,
      sub_specialization: specialisationDetail?.subCategory,
    });
    setPage(1);
    setSelectedFilter(specialisationDetail?.id);
  }

  function handleScroll(direction) {
    const scrollAmount = 800;
    const container = scrollContainerRef.current;

    if (container) {
      const currentScrollLeft = container.scrollLeft;
      let targetScrollLeft;

      if (direction === "left") {
        targetScrollLeft = currentScrollLeft - scrollAmount;
      } else if (direction === "right") {
        targetScrollLeft = currentScrollLeft + scrollAmount;
      }

      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth", // Add smooth scrolling behavior
      });
    }
  }

  function changePrevPage() {
    sendTrackingEvent({
      event: "CE_click-previous-job-cdp",
    });
    if (page <= 1) return;
    setPage(page - 1);
  }

  function changeNextPage() {
    sendTrackingEvent({
      event: "CE_click-next-job-cdp",
    });
    if (totalPages === page) return;
    setPage(page + 1);
  }

  return (
    <SectionContainer
      aria-label="company listed jobs section"
      id="CompanyJobs"
      ref={ref}
    >
      <TextStyled
        variant="h2"
        fontSize="1.25rem"
        fontWeight={700}
        textTransform="capitalize"
        aria-label="company listed jobs section title"
        color={"#000"}
      >
        Our Jobs
      </TextStyled>

      <FlexContainer flexDirection={"column"} margin="1rem 0">
        <TextStyled
          variant="h3"
          fontSize="1em"
          fontWeight={700}
          aria-label="filter by specialisation header"
        >
          Filter by Specialisation
        </TextStyled>

        {isClient && specialisation ? (
          <FlexContainer
            position="relative"
            flexDirection={"column"}
            marginTop="0.5rem"
          >
            {isDesktop && !showButtonLeft && (
              <OverlayContainer position={"left"}>
                <ButtonContainer position={"left"}>
                  <ButtonStyled
                    style={{ height: "2.25rem", width: "2.25rem" }}
                    onClick={() => handleScroll("left")}
                    id="prev-specialization-list"
                  >
                    <ArrowLeftIconStyled />
                  </ButtonStyled>
                </ButtonContainer>
              </OverlayContainer>
            )}
            {isDesktop && !showButtonRight && (
              <OverlayContainer position={"right"}>
                <ButtonContainer position={"right"}>
                  <ButtonStyled
                    style={{ height: "2.25rem", width: "2.25rem" }}
                    onClick={() => handleScroll("right")}
                    id="next-specialization-list"
                  >
                    <ArrowRightIconStyled />
                  </ButtonStyled>
                </ButtonContainer>
              </OverlayContainer>
            )}
            <HScrollContainer
              direction="row"
              gap="0.5rem"
              ref={scrollContainerRef}
            >
              <FilterChip
                selected={selectedFilter === ""}
                onClick={(e) => handleFilterClick(e, "")}
                ref={refLeft}
              >
                All
              </FilterChip>
              {filteredSpecialisation.map((specialisation, index) => (
                <FilterChip
                  key={specialisation.id}
                  selected={selectedFilter === specialisation.id}
                  onClick={(e) => handleFilterClick(e, specialisation)}
                  ref={lastFilterItem === index ? refRight : null}
                >
                  {specialisation.name}
                </FilterChip>
              ))}
            </HScrollContainer>
          </FlexContainer>
        ) : null}
      </FlexContainer>
      <FlexContainer flexDirection={"column"} alignItems="center" gap="1rem">
        <FlexContainer alignItems="center" gap="1rem" width={"100%"}>
          {isDesktop && totalPages > 1 ? (
            totalPages >= page && page > 1 ? (
              <ButtonStyled
                onClick={changePrevPage}
                style={{ marginLeft: "-3.35rem" }}
              >
                <ArrowLeftIconStyled />
              </ButtonStyled>
            ) : null
          ) : null}
          <FlexContainer width={"100%"}>
            <JobContainer>
              {companyJobsList?.map((job, index) => {
                return <CompanyJobCard job={job} key={index} index={index} />;
              })}
            </JobContainer>
          </FlexContainer>
          {isDesktop && totalPages > 1 ? (
            totalPages >= page && totalPages != page ? (
              <ButtonStyled
                onClick={changeNextPage}
                style={{ marginRight: "-3.35rem" }}
              >
                <ArrowRightIconStyled />
              </ButtonStyled>
            ) : null
          ) : null}
        </FlexContainer>
        {isDesktop ? (
          <PaginationContainer>
            {Array.from({ length: totalPages }).map((_, index) => {
              return (
                <PaginationButton
                  key={index}
                  style={
                    page === index + 1
                      ? { backgroundColor: Color.black }
                      : { backgroundColor: Color.white }
                  }
                />
              );
            })}
          </PaginationContainer>
        ) : null}
      </FlexContainer>
    </SectionContainer>
  );
}

//render company job card
function CompanyJobCard(props) {
  const { job, index } = props;

  const dispatch = useDispatch();

  const [hover, setHover] = useState(false);
  const [targetJob, setTargetJob] = useState(null);
  const checkSpace = job?.title.includes(" ");

  const bookmarking = useSelector(
    (state) => state.companies.bookmarkingCompanyJob
  );
  const unbookmarking = useSelector(
    (state) => state.companies.unbookmarkingCompanyJob
  );

  const workExp =
    job?.minYearsExperience + " - " + job?.maxYearsExperience + " Years of Exp";

  function handleJobBookmark(e, job) {
    e.preventDefault();
    e.stopPropagation();

    const loggedIn = checkAuthentication();

    if (!loggedIn) {
      return dispatch(updateSelectedJob(job)).then(() => {
        dispatch(updateSignUpOrigin("save_job"));
        dispatch(updateShowSignInModalStatus(true));
      });
    }

    setTargetJob(job?.id);

    if (!job?.bookmark) {
      dispatch(bookmarkJobCompany({ jobId: job?.id }));
      sendTrackingEvent({
        event: "CE_save-job-cdp",
        "job-id": job?.id,
        featured: job?.boosted,
      });

      toast.success("Job bookmarked");
      setTargetJob(null);
    } else if (job?.bookmark) {
      dispatch(unBookmarkJobCompany({ jobId: job?.id }));

      sendTrackingEvent({
        event: "CE_unsave-job-cdp",
        "job-id": job?.id,
        featured: job?.boosted,
      });

      toast.success("Job unbookmarked");
      setTargetJob(null);
    }
  }

  function handleEventJobCard(job) {
    sendTrackingEvent({
      event: "CE_click-job-card-cdp",
      job_id: job?.id,
      featured: job?.boosted,
    });

    Cookies.set("click-source", "company_jobs");
  }

  return (
    <JobCard
      href={`/jobs/${job?.slug}`}
      rel="noopener noreferrer"
      target="_blank"
      onClick={() => handleEventJobCard(job)}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <FlexContainer alignItems="center" justifyContent="space-between">
        <TextStyled
          color={hover ? Color.black : "rgba(0, 0, 0, 0.60)"}
          variant="h3"
          fontSize="0.875rem"
          fontWeight={700}
          letterSpacing={"0.1px"}
          textTransform="capitalize"
          id="spec-job-title"
          sx={{ wordBreak: !checkSpace && "break-all" }}
        >
          {job?.title}
        </TextStyled>

        {job?.boosted ? (
          <HighlightChip>
            <SparklesIconStyled />
            HOT
          </HighlightChip>
        ) : null}
      </FlexContainer>
      <FlexContainer flexDirection={"column"}>
        <FlexContainer marginTop="0.62rem" gap={"0.375rem"}>
          <JobInfoChip
            style={
              hover
                ? { color: Color.blue, background: "rgba(33, 61, 181, 0.14)" }
                : null
            }
            id="spec-job-type-chip"
          >
            {job?.jobType}
          </JobInfoChip>
          <JobInfoChip
            style={
              hover
                ? {
                    color: "rgba(81, 42, 204, 1)",
                    background: "rgba(81, 42, 204, 0.14)",
                  }
                : null
            }
            id="spec-job-location-chip"
          >
            {job?.stateRegion}
          </JobInfoChip>
          {job.minYearsExperience === null ||
          job.maxYearsExperience === null ||
          (job.minYearsExperience <= 0 &&
            job.maxYearsExperience === 0) ? null : (
            <JobInfoChip
              style={
                hover
                  ? {
                      color: "rgba(192, 132, 12, 1)",
                      background: "rgba(242, 175, 41, 0.14)",
                    }
                  : null
              }
              id="spec-job-exp-chip"
            >
              {workExp}
            </JobInfoChip>
          )}
        </FlexContainer>
        <FlexContainer
          alignItems="center"
          justifyContent="space-between"
          marginTop="0.88rem"
        >
          <FlexContainer alignItems="center" gap="0.75rem">
            <CompanyLogo
              src={job?.company?.logo}
              width={26}
              height={26}
              alt={job?.company?.name + " company logo"}
              id="spec-job-company-logo"
            />
            <TextStyled
              variant="h4"
              color={Color.darkGrey}
              fontSize="0.625rem"
              fontWeight={400}
              letterSpacing="0.09375rem"
              textTransform={"uppercase"}
              textOverflow={"ellipsis"}
              whiteSpace={"nowrap"}
              overflow={"hidden"}
              width={"15.5rem"}
              id="spec-job-company-name"
            >
              {job?.company?.name}
            </TextStyled>
          </FlexContainer>
          {(bookmarking || unbookmarking) && job?.id === targetJob?.id ? (
            <CircularProgressStyled style={{ width: "20px", height: "20px" }} />
          ) : job?.bookmark ? (
            <BookmarkFilledIconStyled
              onClick={(e) => handleJobBookmark(e, job)}
              width="20px"
              height="20px"
              stroke={Color.hiredlyPurple}
              id="spec-job-bookmark"
            />
          ) : (
            <BookmarkIconStyled
              onClick={(e) => handleJobBookmark(e, job)}
              width="20px"
              height="20px"
              stroke={Color.hiredlyPurple}
              id="spec-job-bookmark"
            />
          )}
        </FlexContainer>
      </FlexContainer>
    </JobCard>
  );
}

export default CompanyJobs;
